import React, { useState } from 'react';
import { Link2, Sparkles, AlertTriangle, Info } from 'lucide-react';
import { Input } from '@/components/Input';
import { Button } from '@/components/Button';
import { Platform } from '@/types';
import { generateAffiliateLink } from '@/services/AffiliateService';
import { ApiError, HistoryItem } from '@/types';
import { Session } from '@supabase/supabase-js';
import { PlatformSelector } from '@/components/PlatformSelector';
import { GeneratedResult } from '@/components/GeneratedResult';
import { supabase } from '@/lib/supabase';

interface GeneratorSectionProps {
    session: Session | null;
    addToHistory: (item: HistoryItem) => void;
    isMobile: boolean;
    onAuthOpen: () => void;
}

export const GeneratorSection: React.FC<GeneratorSectionProps> = ({ session, addToHistory, isMobile, onAuthOpen }) => {
    const [url, setUrl] = useState('');
    const [platform, setPlatform] = useState<Platform>('mercadolivre');
    const [loading, setLoading] = useState(false);
    const [loadingText, setLoadingText] = useState('Iniciando...');
    const [result, setResult] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);

    const generatorSectionRef = React.useRef<HTMLDivElement>(null);

    const loadingSteps = [
        "Analisando produto...",
        "Verificando vendedor...",
        "Buscando melhores condições...",
        "Otimizando URL segura...",
        "Finalizando..."
    ];

    const [warning, setWarning] = useState<string | null>(null);

    // Parse Android/PWA Share Target Intent
    React.useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const shareText = params.get('text') || '';
        const shareTitle = params.get('title') || '';
        const shareUrl = params.get('url') || '';

        const combinedText = `${shareTitle} ${shareText} ${shareUrl}`;
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        const matches = combinedText.match(urlRegex);

        if (matches && matches.length > 0) {
            const detectedUrl = matches[matches.length - 1]; // Geralmente o último link da string é o correto (Shopee bota 2 as vezes)
            setUrl(detectedUrl);

            // Auto-detect platform logic
            let detectedPlatform: Platform = 'mercadolivre';
            const lowerUrl = detectedUrl.toLowerCase();
            if (lowerUrl.includes('amazon') || lowerUrl.includes('amzn') || lowerUrl.includes('/a.co/')) {
                detectedPlatform = 'amazon';
                setWarning("Plataforma desativada no momento.");
            } else if (lowerUrl.includes('mercadolivre') || lowerUrl.includes('mercadona') || lowerUrl.includes('meli')) {
                detectedPlatform = 'mercadolivre';
                setWarning(null);
            } else if (lowerUrl.includes('shopee') || lowerUrl.includes('shp.ee')) {
                detectedPlatform = 'shopee';
                setWarning(null);
            } else if (lowerUrl.includes('aliexpress') || lowerUrl.includes('ali.ski') || lowerUrl.includes('a.aliexpress')) {
                detectedPlatform = 'aliexpress';
                setWarning(null);
            } else if (lowerUrl.includes('magazinevoce') || lowerUrl.includes('magazineluiza') || lowerUrl.includes('magalu')) {
                detectedPlatform = 'magalu';
                setWarning(null);
            }
            setPlatform(detectedPlatform);

            // Clean URL and remove params
            const cleanUrl = window.location.protocol + "//" + window.location.host + window.location.pathname;
            window.history.replaceState({ path: cleanUrl }, '', cleanUrl);

            if (warning === null) {
                if (generatorSectionRef.current) {
                    generatorSectionRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
                }

                executeGeneration(detectedUrl, detectedPlatform);
            }
        }
    }, []);

    const handlePlatformSelect = (newPlatform: Platform) => {
        setPlatform(newPlatform);
        setUrl('');
        setError(null);
        setResult(null);
        setWarning(null);
    };

    const executeGeneration = async (targetUrl: string, targetPlatform: Platform) => {
        setError(null);
        setResult(null);
        setWarning(null);

        if (!targetUrl) {
            setError('Por favor, insira a URL do produto.');
            return;
        }

        setLoading(true);
        let stepIndex = 0;

        const interval = setInterval(() => {
            setLoadingText(loadingSteps[stepIndex]);
            stepIndex = (stepIndex + 1) % loadingSteps.length;
        }, 600);

        try {
            const start = Date.now();
            let currentToken = session?.access_token;
            if (!currentToken) {
                const { data } = await supabase.auth.getSession();
                currentToken = data?.session?.access_token;
            }

            const response = await generateAffiliateLink(
                { url: targetUrl, platform: targetPlatform },
                currentToken
            );
            const elapsed = Date.now() - start;

            if (elapsed < 900) {
                const remainingTime = 900 - elapsed;
                await new Promise(resolve => setTimeout(resolve, remainingTime));
            }

            let shortUrl = response.short_url;
            setResult(shortUrl);

            // Set warning if present in response
            if ((response as any).warning) {
                setWarning((response as any).warning);
            }

            addToHistory({
                id: crypto.randomUUID(),
                originalUrl: targetUrl,
                shortUrl: shortUrl,
                platform: targetPlatform,
                date: new Date().toISOString()
            });

        } catch (err: any) {
            const apiError = err as ApiError;
            console.error(err);
            setError(apiError.message || 'Não foi possível otimizar este link no momento.');
        } finally {
            clearInterval(interval);
            setLoading(false);
        }
    };

    const handleGenerate = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        await executeGeneration(url, platform);
    };

    const resultRef = React.useRef<HTMLDivElement>(null);

    React.useEffect(() => {
        if (result && resultRef.current) {
            resultRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
    }, [result]);

    return (
        <div className="glass-card rounded-3xl border border-white/50 dark:border-gray-700/50 shadow-2xl shadow-blue-900/10 dark:shadow-black/50 animate-in zoom-in-95 duration-500 max-w-4xl mx-auto">
            <div className="bg-white/50 dark:bg-gray-900/50 rounded-[20px] p-2 sm:p-4 backdrop-blur-sm">

                <div className="text-center mb-8">
                    <div className="flex items-center justify-center gap-2 mb-2">
                        <Sparkles className="w-5 h-5 text-pink-500" />
                        <h2 className="text-lg font-bold bg-clip-text text-transparent bg-gradient-to-r from-pink-500 to-violet-500">
                            Otimizador de Ofertas
                        </h2>
                    </div>
                    <p className="text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto leading-relaxed">
                        Insira o link do produto aqui. Transformamos ele em um link otimizado e seguro para você aproveitar a oferta.
                    </p>
                </div>

                <PlatformSelector platform={platform} onSelect={handlePlatformSelect} />

                <form onSubmit={handleGenerate} className="space-y-6 mb-6">
                    <Input
                        label="URL DO PRODUTO"
                        placeholder={
                            platform === 'mercadolivre' ? "https://www.mercadolivre.com.br/produto/..." :
                                platform === 'amazon' ? "https://www.amazon.com.br/dp/..." :
                                    platform === 'shopee' ? "https://shopee.com.br/..." :
                                        platform === 'aliexpress' ? "https://pt.aliexpress.com/item/..." :
                                            platform === 'magalu' ? "https://www.magazineluiza.com.br/..." :
                                                "Cole o link do produto aqui..."
                        }
                        value={url}
                        onChange={(e) => {
                            const newUrl = e.target.value;
                            setUrl(newUrl);

                            // Auto-detect platform
                            const lowerUrl = newUrl.toLowerCase();
                            if (lowerUrl.includes('amazon') || lowerUrl.includes('amzn') || lowerUrl.includes('/a.co/')) {
                                setPlatform('amazon');
                                setWarning("Plataforma desativada no momento."); // Clear warning on auto-switch
                            } else if (lowerUrl.includes('mercadolivre') || lowerUrl.includes('mercadona') || lowerUrl.includes('meli')) {
                                setPlatform('mercadolivre');
                                setWarning(null);
                            } else if (lowerUrl.includes('shopee') || lowerUrl.includes('shp.ee')) {
                                setPlatform('shopee');
                                setWarning(null);
                            } else if (lowerUrl.includes('aliexpress') || lowerUrl.includes('ali.ski') || lowerUrl.includes('a.aliexpress')) {
                                setPlatform('aliexpress');
                                setWarning(null);
                            } else if (lowerUrl.includes('magazinevoce') || lowerUrl.includes('magazineluiza') || lowerUrl.includes('magalu')) {
                                setPlatform('magalu');
                                setWarning(null);
                            }
                        }}
                        icon={<Link2 className="w-5 h-5" />}
                        type="text"
                        required
                    />

                    <Button
                        type="submit"
                        className="w-full text-base h-14"
                        isLoading={loading}
                        loadingText={loadingText}
                        variant="primary"
                        disabled={warning === "Plataforma desativada no momento."}
                    >
                        {!loading && <Sparkles className="w-5 h-5 mr-1" />}
                        {loading ? '' : 'Otimizar Agora'}
                    </Button>
                    <div ref={generatorSectionRef} />
                </form>

                {error && (
                    <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex items-center gap-3 text-red-600 dark:text-red-400 animate-in fade-in slide-in-from-top-2">
                        <div className="p-2 bg-red-500/20 rounded-full shrink-0">
                            <AlertTriangle className="w-5 h-5" />
                        </div>
                        <p className="text-sm font-medium leading-tight">{error}</p>
                    </div>
                )}

                {warning && (
                    <div className="mt-6 p-4 bg-amber-500/10 border border-amber-500/20 rounded-xl flex gap-3 text-amber-600 dark:text-amber-500 text-sm animate-in fade-in slide-in-from-top-2">
                        <AlertTriangle className="w-5 h-5 shrink-0" />
                        <div>
                            <p className="font-semibold mb-1">Atenção:</p>
                            <p>{warning}</p>
                            <p className="mt-2 text-xs opacity-80">Dúvidas? Entre em contato: <a href="mailto:contato@radardaeconomia.com.br" className="underline hover:text-amber-700 dark:hover:text-amber-300">contato@radardaeconomia.com.br</a></p>
                        </div>
                    </div>
                )}

                {!session && result && (
                    <div className="mt-6 bg-blue-500/10 border border-blue-500/20 rounded-xl p-4 flex gap-3 text-blue-600 dark:text-blue-400 text-sm animate-in fade-in slide-in-from-top-2">
                        <Info className="w-5 h-5 shrink-0" />
                        <div>
                            <p className="font-semibold mb-1">Você não está logado</p>
                            <p>Este link será gerado normalmente, mas para acumular benefícios do Radar da Economia, <button type="button" onClick={onAuthOpen} className="underline hover:text-blue-800 dark:hover:text-blue-300 font-medium">entre gratuitamente</button></p>
                        </div>
                    </div>
                )}

                {result && (
                    <div ref={resultRef} className={`space-y-6 ${platform === 'magalu' ? 'mb-6' : ''}`}>
                        <GeneratedResult result={result} isMobile={isMobile} />

                        <div className="flex justify-center animate-in fade-in slide-in-from-bottom-4 duration-700 delay-200">
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setUrl('');
                                    setResult(null);
                                    setError(null);
                                    setWarning(null);
                                }}
                                className="w-auto px-8"
                            >
                                Gerar outro link
                            </Button>
                        </div>
                    </div>
                )}

                {platform === 'magalu' && (
                    <div className="text-center p-6 sm:p-8 bg-blue-50/50 dark:bg-blue-900/10 rounded-2xl border border-blue-100 dark:border-blue-800/30 mb-2 animate-in fade-in zoom-in-95 duration-300">
                        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                            <Sparkles className="w-8 h-8 text-blue-600 dark:text-blue-400" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Compre na nossa Loja Oficial Magalu</h3>
                        <p className="text-gray-600 dark:text-gray-400 mb-6 max-w-lg mx-auto">
                            Transformamos qualquer link da Magazine Luiza para a nossa loja parceira. Ou se preferir, acesse diretamente clicando abaixo.
                        </p>
                        <a
                            href="https://www.magazinevoce.com.br/magazineradardaeconomia/"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-xl transition-all shadow-lg shadow-blue-500/30 hover:shadow-blue-500/50"
                        >
                            Acessar Loja Magalu
                            <Link2 className="w-5 h-5" />
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
};
